import { FC } from "react";

import { Stack } from "@mui/material";
import { ORG_PAGE_CONTACT_MESSAGE_MAX_LENGTH } from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";
import { isEmail } from "validator";

import { ContactUsFormData } from "../helpers/useContactUsForm";

import { isEmailAndAllowPlus, isRequired } from "@/common/helpers/validators";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormDialog } from "@/forms-design-system/FormDialog";
import { FormInfobox } from "@/forms-design-system/FormInfobox";
import { FormTextField } from "@/forms-design-system/FormTextField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Mail, Success } from "@/icons/outlined";

interface ContactUsDialogProps {
  organizationName: string;
  form: UseFormReturn<ContactUsFormData>;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  sendErrorMessage: string | null;
  sentToEmail: string | null;
  inline?: boolean;
}

export const ContactUsDialog: FC<ContactUsDialogProps> = ({
  organizationName,
  form,
  open,
  onClose,
  onSubmit,
  sendErrorMessage,
  sentToEmail,
  inline,
}) => {
  const { t } = useTranslate();
  const [senderEmail, message] = useWatch({ control: form.control, name: ["senderEmail", "message"] });
  const canSend = isEmail(senderEmail.trim()) && message.trim().length > 0;

  if (sentToEmail) {
    return (
      <FormDialog
        open={open}
        inline={inline}
        onClose={onClose}
        size="xs"
        icon={<Success />}
        title={t("organization", "contactUs.sentTitle")}
        actions={[
          <FormButton key="close" onClick={onClose}>
            {t("common", "close")}
          </FormButton>,
        ]}
      >
        <FormTypography variant="body2" vibe="moderate">
          <Trans
            space="organization"
            i18nKey="contactUs.sentDescription"
            values={{ organizationName, email: sentToEmail }}
            components={{ bold: <FormTypography variant="subtitle2" component="span" /> }}
          />
        </FormTypography>
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      inline={inline}
      onClose={onClose}
      size="xs"
      icon={<Mail />}
      title={t("organization", "contactUs.dialogTitle", { organizationName })}
      actions={[
        <FormButton
          key="send"
          onClick={onSubmit}
          disabled={!canSend}
          isLoading={form.formState.isSubmitting}
          data-test="org-page-contact-us-send"
        >
          {t("organization", "contactUs.send")}
        </FormButton>,
        <FormButton key="cancel" variant="outline" onClick={onClose}>
          {t("common", "cancel")}
        </FormButton>,
      ]}
    >
      <Stack gap={1.5}>
        <FormTextField
          control={form.control}
          name="senderEmail"
          type="email"
          label={t("organization", "contactUs.emailLabel")}
          placeholder={t("organization", "contactUs.emailPlaceholder")}
          rules={[
            isRequired(t("common", "isRequired")),
            isEmailAndAllowPlus(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
          ]}
          data-test="org-page-contact-us-email"
        />
        <FormTextField
          control={form.control}
          name="message"
          multiline
          rows={5}
          label={t("organization", "contactUs.messageLabel")}
          placeholder={t("organization", "contactUs.messagePlaceholder")}
          currentLength={message.length}
          maxLength={ORG_PAGE_CONTACT_MESSAGE_MAX_LENGTH}
          inputProps={{ maxLength: undefined }}
          rules={[
            isRequired(t("common", "isRequired")),
            {
              maxLength: {
                value: ORG_PAGE_CONTACT_MESSAGE_MAX_LENGTH,
                message: t("organization", "contactUs.messageTooLong", { max: ORG_PAGE_CONTACT_MESSAGE_MAX_LENGTH }),
              },
            },
          ]}
          data-test="org-page-contact-us-message"
        />
        {sendErrorMessage && <FormInfobox variant="error" description={sendErrorMessage} />}
      </Stack>
    </FormDialog>
  );
};
